/**
 * Easement news and updates, written from free official sources.
 *
 * Rules for this file:
 *  - Every factual claim traces to a source in the post's `sources` array.
 *  - Sources are official first: court opinion portals, state legislature
 *    sites, county recorder and clerk offices. All HTTPS.
 *  - No invented case names, holdings, dates, or dollar figures.
 */

import { BLOG_GUIDES } from "./blogGuides";
import { NORTH_DAKOTA_RIGHTS_POST } from "./blogNorthDakota";
import { BLOG_LAND_USE_GUIDES } from "./blogLandUseGuides";
import { BLOG_RIGHTS_GUIDES } from "./blogRightsGuides";
import { RURAL_LAND_DUE_DILIGENCE_POST } from "./blogRuralLand";

export type BlogTopic =
  | "State law"
  | "Utility corridors"
  | "County recording"
  | "Emerging technology"
  | "Property rights";

export const BLOG_TOPICS: BlogTopic[] = [
  "State law",
  "Utility corridors",
  "County recording",
  "Emerging technology",
  "Property rights",
];

export type BlogSource = {
  label: string;
  url: string;
  note?: string;
};

export type BlogSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  /** ISO date the post was published. */
  publishedOn: string;
  topic: BlogTopic;
  /** One-sentence summary used on the index and in meta descriptions. */
  summary: string;
  /** Opening paragraphs, before the first heading. */
  intro: string[];
  sections: BlogSection[];
  /** Practical takeaways for a property owner. */
  whatItMeansForYou: string[];
  sources: BlogSource[];
  /** Slugs from src/data/homeowner-questions.ts. */
  relatedQuestions: string[];
  /** State slugs linking to /easement-law/$state. */
  relatedStates?: string[];
  /** County guides linking to /easement-guides/$state/$county. */
  relatedCountyGuides?: { state: string; county: string; label: string }[];
};

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "ohio-supreme-court-herbicide-utility-easement",
    title: "Ohio's high court reads a 1948 utility easement to allow herbicide spraying",
    publishedOn: "2026-09-11",
    topic: "Utility corridors",
    summary:
      "In Corder v. Ohio Edison Co., the Supreme Court of Ohio held that an easement letting a utility trim, cut and remove trees and other obstructions also lets it use herbicide inside the corridor.",
    intro: [
      "If a power line crosses your land, the wording of the easement decides far more than where the poles sit. It also decides how the utility is allowed to keep the corridor clear, and that question reaches courts more often than most owners expect.",
      "On November 20, 2024, the Supreme Court of Ohio decided Corder v. Ohio Edison Co., 2024-Ohio-5432. The court read easements granted in 1948 that gave the utility the right to trim, cut and remove trees, limbs, underbrush or other obstructions, and held that language covers controlling vegetation with herbicide. The appeals court's judgment was reversed and the case sent back with instructions to enter summary judgment for the utility company.",
    ],
    sections: [
      {
        heading: "What the dispute was about",
        paragraphs: [
          "The Corder family owns land in Harrison County, Ohio, crossed by easements Ohio Edison has held since 1948. The grant is the kind of short, broad clause common in mid-century utility deeds: the right to trim, cut and remove trees, limbs, underbrush or other obstructions within the corridor.",
          "The landowners objected to the utility applying herbicide rather than cutting by hand or by machine. The written grant says nothing about chemicals, because in 1948 nobody drafting it was thinking about them. So the question became whether a right stated in terms of trimming, cutting and removing is a right about the outcome (a clear corridor) or a right about the method (blades only).",
          "This case had already been to the state's high court once. In 2020, the court held that a common pleas court, not the Public Utilities Commission of Ohio, has jurisdiction to decide what the scope of a utility easement is, because that is a property-rights question rather than a rate or service question. The 2024 decision answered the property question the 2020 decision sent back down.",
        ],
      },
      {
        heading: "How courts read old easement language",
        paragraphs: [
          "The reasoning matters more than the herbicide. When a recorded grant describes a purpose in general terms, courts commonly read it as covering the reasonable means of accomplishing that purpose, including methods that did not exist or were not customary when the document was signed. A grant that lists a result usually is not treated as a closed list of tools.",
          "That principle cuts both ways for an owner. A broad clause gives the holder room to modernize. A narrow, specific clause, one that names a width, a method, a schedule or an approval step, holds the holder to what it says. Which one is on your deed is a matter of the exact words recorded decades ago, not of what feels fair now.",
          "Ohio law governs the Corder outcome, and other states weigh scope questions on their own precedent. But the drafting lesson travels: the specificity of your easement text is the practical limit on what the holder may do.",
        ],
        bullets: [
          "Broad purpose language tends to permit modern methods of achieving the same purpose.",
          "Named limits (width in feet, notice requirements, listed activities) are the ones that hold.",
          "Silence in a grant is usually read in favor of the holder's reasonable use, not against it.",
        ],
      },
      {
        heading: "Reading your own utility easement",
        paragraphs: [
          "Pull the recorded instrument, not the plat note or the title commitment summary. The commitment tells you an easement exists and where it is indexed; only the recorded grant tells you what the holder may do inside it.",
          "Look for four things: the stated purpose, the described width and location, the maintenance rights, and any restrictions on the owner. A grant that reads \"a strip 30 feet in width\" is measurable on the ground. A grant that reads \"such width as is reasonably necessary\" is not, and its footprint may effectively be set by the equipment the holder uses.",
          "If your grant is a short mid-century clause with no method limits, assume the holder has latitude on how it clears vegetation, and plan plantings and structures accordingly rather than relying on an objection after the crew arrives.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Find the recorded utility easement for your parcel and read the maintenance sentence word for word.",
      "Do not plant trees or build inside a corridor on the assumption that clearing will be done by hand.",
      "If a notice or approval step is written into your grant, hold the holder to it in writing before work begins.",
      "Ask the utility for its published vegetation-management standard; many post cycle lengths and treatment methods.",
      "Where the corridor's width is vague, have it located by survey before you site a shed, fence or driveway near it.",
    ],
    sources: [
      {
        label: "Corder v. Ohio Edison Co., 2024-Ohio-5432 (Supreme Court of Ohio, Nov. 20, 2024)",
        url: "https://www.supremecourt.ohio.gov/rod/docs/pdf/0/2024/2024-Ohio-5432.pdf",
        note: "Full opinion, including the syllabus describing the herbicide question and the reversal.",
      },
      {
        label: "Corder v. Ohio Edison Co., 2020-Ohio-5220 (Supreme Court of Ohio)",
        url: "https://www.supremecourt.ohio.gov/rod/docs/pdf/0/2020/2020-Ohio-5220.pdf",
        note: "Earlier decision holding that a common pleas court, not the utilities commission, decides easement scope.",
      },
    ],
    relatedQuestions: [
      "can-a-utility-company-cut-down-my-trees",
      "can-i-plant-trees-in-a-utility-easement",
      "how-wide-is-a-utility-easement",
      "utility-corridor-requirements",
    ],
  },
  {
    slug: "wyoming-conservation-easement-duration-bill",
    title: "Wyoming's proposed 30-year cap on conservation easements did not advance",
    publishedOn: "2026-09-11",
    topic: "State law",
    summary:
      "House Bill 220 in Wyoming's 2025 session would have limited new conservation easements to 30 years; the bill was not taken up for introduction, so perpetual easements remain the norm there.",
    intro: [
      "Most conservation easements in the United States are perpetual. That permanence is the point for the land trusts and agencies that hold them, and it is also the feature that draws periodic legislative attention, because it binds owners who have not been born yet.",
      "Wyoming's 2025 session saw one of those attempts. House Bill 220, titled \"Conservation easements-amendments,\" proposed limiting the duration of conservation easements to 30 years, with definitions and conforming changes to match. It did not become law: the legislature's own history for the bill shows it was received for introduction on January 17, 2025, and then not considered for introduction on February 3, 2025.",
    ],
    sections: [
      {
        heading: "What the bill would have done",
        paragraphs: [
          "The bill's own description states its purpose plainly: an act relating to property, conveyances and security transactions, limiting the duration of conservation easements to 30 years, providing definitions, making conforming amendments, specifying applicability, and providing for an effective date.",
          "A duration cap changes the character of the instrument. A perpetual conservation easement is a permanent reduction in the bundle of rights attached to the land, which is why it supports a deduction or a purchase price today. A term easement is closer to a long lease of development rights, and both its tax treatment and its market value are different.",
          "Because the bill was never taken up, none of that took effect in Wyoming. Conservation easements there continue to be governed by existing state law, and a perpetual grant remains enforceable as written.",
        ],
      },
      {
        heading: "Why bills like this keep appearing",
        paragraphs: [
          "The recurring argument for a cap is generational: an owner today can restrict a parcel forever, and heirs who inherit the land inherit a decision they had no part in. The recurring argument against a cap is that conservation buyers, whether a land trust, a state program or a federal one, are paying for permanence and will not pay the same for 30 years.",
          "There is also a records dimension. Instruments creating or transferring conservation easements are recorded in the county land records, and a duration limit means a recorded document with an expiration a future title examiner has to catch. Perpetual easements do not have that failure mode.",
          "Whatever the merits, an owner should track this kind of bill by its actual disposition rather than its headline. Introduction, committee passage and enactment are three different things, and legislature sites publish the step-by-step history for each bill.",
        ],
        bullets: [
          "A perpetual easement runs with the land and binds every later owner.",
          "A term easement expires, which changes both valuation and the title record.",
          "A bill's status page, not a news summary, is the reliable source for whether it passed.",
        ],
      },
      {
        heading: "Checking the status of any easement bill yourself",
        paragraphs: [
          "Every state legislature publishes bill text and an action history for free. Search by bill number or by keyword, open the bill's page, and read the dated list of actions. Terms like \"did not consider for introduction,\" \"died in committee\" or \"failed\" mean nothing changed in the law.",
          "If a bill did pass, the next step is finding where it landed in the code. Enacted bills amend numbered statute sections, and the state's published code, not the bill, is the text that governs. Our state easement law pages link directly to those code sections.",
          "For conservation easements specifically, also check whether the holder is a land trust, a state agency or a federal program, because program rules can impose requirements the state statute does not.",
        ],
      },
    ],
    whatItMeansForYou: [
      "In Wyoming, nothing changed in 2025: a perpetual conservation easement is still perpetual.",
      "If you are considering granting one, read the duration clause and confirm whether it is perpetual or for a term.",
      "Ask the holder in writing how amendment and termination work before you sign anything.",
      "If you are buying land already under a conservation easement, get the recorded instrument and any baseline documentation report.",
      "Treat legislative proposals as proposals until the legislature's own action history shows enactment.",
    ],
    sources: [
      {
        label: "Wyoming HB0220 (2025), Conservation easements-amendments",
        url: "https://www.wyoleg.gov/Legislation/2025/HB0220",
        note: "Official bill page with sponsors and current text.",
      },
      {
        label: "Wyoming HB0220 digest and action history",
        url: "https://wyoleg.gov/2025/Digest/HB0220.pdf",
        note: "Dated action list showing the bill was not considered for introduction on February 3, 2025.",
      },
      {
        label: "Colorado Revised Statutes 38-30.5-106, recordation of conservation easements",
        url: "https://leg.colorado.gov/sites/default/files/images/olls/crs2024-title-38.pdf",
        note: "Example of a state recording requirement for conservation easements in gross.",
      },
    ],
    relatedQuestions: [
      "what-is-a-conservation-easement",
      "can-an-easement-expire",
      "do-easements-transfer-with-the-property",
      "can-i-be-paid-for-an-easement-on-my-land",
    ],
  },
  {
    slug: "county-erecording-expansion-easement-documents",
    title: "More counties opened electronic recording in 2025, and it changes how easements get filed",
    publishedOn: "2026-09-11",
    topic: "County recording",
    summary:
      "County recorders and clerks across several states brought eRecording online during 2025, shortening the gap between signing an easement and finding it in the public index.",
    intro: [
      "An easement is only as good as its place in the public record. Until an instrument is recorded and indexed, a later buyer or lender can miss it, and the owner relying on it can be left arguing about notice.",
      "During 2025, a series of county offices added or widened electronic recording for land records. Sumter County, Florida's clerk announced eRecording of land records in July 2025, describing paper-origin documents as being scanned into recording order and fully integrated electronic submissions processing in seconds. Kalamazoo County, Michigan's clerk and register of deeds launched a consumer eRecording service the same month, aimed at individuals rather than only title companies. Lake County, California went live on a statewide delivery system on July 1, 2025.",
    ],
    sections: [
      {
        heading: "What eRecording actually changes",
        paragraphs: [
          "Traditional recording is a mail-and-counter process: the signed, notarized original travels to the recorder, gets examined, gets a stamp with book and page or an instrument number, and is returned weeks later. eRecording replaces the transport, not the examination. A submitter uploads an image, the office reviews it against the same statutory requirements, and the stamp comes back the same day in many offices.",
          "For easements, the practical effect is the gap between signature and index entry. A shared driveway agreement signed on a Friday and recorded electronically on Monday is discoverable by a title searcher almost immediately. The same document mailed in may not appear in the index for weeks, and that window is exactly when a competing sale or refinance can slip past it.",
          "It also changes who can file. Most offices historically took electronic submissions only through commercial submitters used by title companies and lenders. Consumer-facing services, like the one Kalamazoo County announced, let an individual owner submit an eligible document directly.",
        ],
        bullets: [
          "Recording standards do not relax: margins, legal descriptions, notarization and fees still control acceptance.",
          "Not every document type is eligible in every county; offices publish their accepted list.",
          "The recording date that matters is the office's, not the date you uploaded.",
        ],
      },
      {
        heading: "Why acceptance lists and rejection reasons matter",
        paragraphs: [
          "Counties publish the document types they take electronically, and those lists are specific. St. Louis County, Minnesota, for example, publishes a coded list of accepted types covering agreements, amendments, assignments, easements-adjacent instruments and more. If your instrument type is not on the list, it goes in on paper.",
          "Rejections are the other half of the story. An electronic submission that fails examination comes back quickly, which is an advantage, but only if someone is watching for it. Common reasons are a missing or defective legal description, an unsigned or improperly notarized page, an incorrect fee, or a missing prepared-by or return-to block.",
          "For an easement specifically, the legal description is where most trouble starts. The description of the easement area has to be sufficient on its own, not merely a reference to a line on a drawing that was never recorded.",
        ],
      },
      {
        heading: "How to confirm your county's current process",
        paragraphs: [
          "Do not rely on a third-party summary, including this one, for the mechanics. Open the recorder, clerk or register of deeds page for the county where the land sits and read three things: whether electronic submission is accepted, which document types are eligible, and the current fee schedule.",
          "Recording fees change without notice, which is why our county guides link to each office's published schedule instead of quoting a dollar figure that goes stale. Call the office if the page is ambiguous; recording staff answer procedural questions routinely.",
          "After recording, get the instrument number and pull the recorded image from the index yourself. A confirmation email from a submitter is not the same as an entry in the public record.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Check your county recorder's page for eRecording eligibility before assuming a document must be mailed.",
      "Record easement agreements promptly; the gap before indexing is when priority problems happen.",
      "Make sure the easement area has its own sufficient legal description, not just a reference to a sketch.",
      "Budget from the office's current fee schedule rather than a figure quoted anywhere else.",
      "Verify the filing afterward by pulling the recorded instrument number from the county index.",
    ],
    sources: [
      {
        label: "Sumter County, Florida Clerk of Courts, electronic filing for official records (July 17, 2025)",
        url: "https://www.sumterclerk.com/public-records/official-records/",
        note: "Clerk announcement that the county is now eRecording land records.",
      },
      {
        label: "Kalamazoo County, Michigan Clerk/Register of Deeds consumer eRecording launch (July 21, 2025)",
        url: "https://www.kalcounty.gov/m/newsflash/Home/Detail/112",
        note: "County news release describing remote submission by individual residents.",
      },
      {
        label: "St. Louis County, Minnesota accepted electronic recording document types",
        url: "https://www.stlouiscountymn.gov/Portals/0/Library/Dept/Public-Records/Recording/Real-Estate-Recording/Document%20types.pdf",
        note: "Example of a published county list of eligible document types.",
      },
      {
        label: "CeRTNA, Lake County, California eRecording go-live (July 1, 2025)",
        url: "https://www.certnaca.gov/2025-07-01-exciting-news-lake-county-goes-live-with-erecording",
        note: "Government eRecording delivery system announcement.",
      },
    ],
    relatedQuestions: [
      "what-does-it-cost-to-record-an-easement",
      "how-do-i-grant-an-easement-on-my-property",
      "how-to-find-easements-on-my-property",
      "how-do-i-find-out-who-owns-an-easement",
    ],
  },
  {
    slug: "drone-delivery-easements-future-technology",
    title:
      "Drone delivery and the easements it will need: a property-law reading of the BVLOS rulemaking",
    publishedOn: "2026-09-23",
    topic: "Emerging technology",
    summary:
      "Federal airspace rules are moving toward routine drone delivery, but the ground rights that make it work, landing pads, rooftop access, power and conduit, corridor overflight, are still ordinary recorded easements negotiated county by county.",
    intro: [
      "Most writing about drone delivery stops at the airspace question: may the aircraft fly there, and under whose authorization. That is the Federal Aviation Administration's subject, and the agency has been working it since Congress directed a beyond-visual-line-of-sight rulemaking in the FAA Reauthorization Act of 2024. The proposed rule, published August 7, 2025 under Docket No. FAA-2025-1908, would create a performance-based pathway for low-altitude operations including package delivery.",
      "But an authorization to fly is not a right to touch land. Every delivery network needs places to take off, land, charge, and be serviced; every autonomous ground robot needs a surface to cross; every sensor mast and fiber run needs a strip of somebody's parcel. Those are property interests, and in the United States they are created, recorded, and disputed under state law in county recorder offices. The same offices that have been recording utility strips for a century.",
      "This article traces where the federal line stops and the recorded-easement line begins, and what an owner, a developer, or a municipality should expect to see appear on title. It is educational; it is not legal advice, and nothing here describes a rule that is in force unless it says so.",
    ],
    sections: [
      {
        heading: "What the federal rulemaking actually proposes",
        paragraphs: [
          "The FAA's proposal is styled 'Normalizing Unmanned Aircraft Systems Beyond Visual Line of Sight Operations' (RIN 2120-AL82). Under the agency's own fact sheet, the operations it would enable include package delivery, agriculture, aerial surveying, and public safety work, conducted at or below 400 feet above ground level and launched from pre-designated, access-controlled locations. Operators would identify the boundaries of their operating area, the approximate number of daily operations, and their takeoff, landing, and loading areas.",
          "Read that list again from a property perspective. 'Pre-designated and access-controlled locations,' 'takeoff, landing and loading areas,' and a defined operating boundary are all descriptions of land. The FAA can approve where an aircraft flies. It cannot convey the right to place a landing pad on a warehouse roof, run a power drop to it, or cross a neighboring lot to service it.",
          "As of this writing the rule is not law. The proposal was published in 2025, its comment periods have closed, and the final rule has not appeared in the Federal Register. Treat every operational detail above as proposed, and check the docket before relying on it.",
        ],
        bullets: [
          "Federal: airspace access, aircraft certification, operator qualification, separation services.",
          "State and local: the right to occupy, cross, build on, or charge from a particular piece of ground.",
          "Recorded easements are how the second category is proven to a title company and a future buyer.",
        ],
      },
      {
        heading: "The airspace question property law already answered",
        paragraphs: [
          "The foundational American case is United States v. Causby, 328 U.S. 256 (1946), where military aircraft flying low over a North Carolina chicken farm destroyed the owner's business. The Supreme Court rejected the ancient idea that ownership runs indefinitely upward, but held that a landowner owns at least as much of the space above the ground as he can occupy or use in connection with the land, and that flights low enough and frequent enough to interfere with that use can amount to a taking requiring compensation.",
          "Causby is why airports acquire and record avigation easements rather than relying on federal airspace authority alone. The same instrument is the obvious template for delivery corridors that repeatedly overfly a single parcel at low altitude. A residential cut-through between a distribution hub and a neighborhood, for example. Whether ordinary package flights ever rise to a Causby-level interference is unsettled, and this article does not predict it. What is certain is that the recorded avigation easement already exists as a tool, and that a network operator who wants certainty will reach for it.",
        ],
      },
      {
        heading: "Six easement types a delivery network is likely to record",
        paragraphs: [
          "None of these are novel instruments. Each is a familiar form being pointed at a new use, which is why they will be recorded in the ordinary county index alongside sewer and power strips.",
        ],
        bullets: [
          "Avigation or overflight easement: the right to fly over a described parcel below a stated altitude, with a noise-and-nuisance waiver. Long-standing airport practice.",
          "Landing pad or vertiport easement: an exclusive area easement over a roof, parking structure, or pad site, with structural and load obligations.",
          "Access and maintenance easement: ground-level ingress and egress to service equipment, usually the clause that outlasts the technology it was written for.",
          "Utility easement for power: charging a fleet is an electrical load, and the serving utility will want its own recorded strip for the transformer and feeder.",
          "Conduit and fiber easement: control links, ground radar, and detect-and-avoid sensors need backhaul; buried conduit is recorded the same way as any other utility line.",
          "Sidewalk and curb access for ground robots: typically a municipal franchise or license rather than a private easement, but where it crosses private frontage it becomes one.",
        ],
      },
      {
        heading: "Why the old strip on your plat matters to the new technology",
        paragraphs: [
          "Existing utility corridors are the most likely skeleton for future networks. They are already continuous, already cleared, already recorded, and already held by entities with condemnation power or franchise rights. The legal question is whether a 1962 easement 'for electric transmission and distribution purposes' reaches a drone charging node or a sensor mast, and courts answer that by reading the granted purpose, not the technology.",
          "That reading problem is not hypothetical. Owners who want to know what their own strip permits should start with the exact recorded language, because the scope fight is always about the words in the instrument, not what the current occupant would like them to mean.",
        ],
      },
      {
        heading: "A worked example: what the record already shows in Las Vegas",
        paragraphs: [
          "Clark County, Nevada publishes its right-of-way and easement layers as a free public map service, which makes it a useful test of how much of this is already visible. Running a lookup against the county's published layer at 500 S Grand Central Parkway in Las Vegas returns several recorded strips over a single downtown block, including a 15-foot sewer easement and a 55-foot drainage easement, all tied back to a recording reference in the county index.",
          "That is what a delivery operator, a title examiner, or an owner is actually working with: a handful of overlapping strips, each with its own width and its own recorded purpose, on a parcel that has to host new equipment somewhere. The site's Las Vegas lookup and the Clark County guide below show the same data, and Clark County's recording formatting rules determine whether a new instrument over that block gets accepted or rejected at the counter.",
        ],
      },
      {
        heading: "The state-law variables that decide the outcome",
        paragraphs: [
          "Because the interest is created under state law, the same drone corridor produces different results in different states. Whether an unrecorded but long-used route can ripen into a prescriptive right, how a court treats an easement whose stated purpose has been overtaken by technology, and what notice a buyer is charged with all vary. Our state profiles set out the distinctive rule, the elements, and the pitfalls for each state with sourced citations; the states most exposed to early delivery networks are linked below.",
          "The county layer matters too. Fee structure, page formatting, margin requirements, legal-description conventions, and e-recording availability differ by recorder, and a rejected instrument is an unrecorded instrument. Which is to say, one that a later purchaser may take free of.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Pull the recorded language of every strip on your parcel before agreeing to host any new equipment; the granted purpose, not the technology, controls what is allowed.",
      "If you are asked to sign an overflight, pad, or access easement, negotiate a defined area, a defined altitude or footprint, a term or termination trigger, and a restoration obligation.",
      "Expect the power and conduit easements to arrive separately from the flight-related one, often months later and from a different grantee.",
      "Check your state profile for how prescriptive use and easement-scope disputes are decided where your property sits.",
      "Confirm your county recorder's formatting rules before recording anything new; a rejection at the counter leaves you unprotected against later buyers.",
    ],
    sources: [
      {
        label: "FAA, Beyond Visual Line of Sight (BVLOS) newsroom page",
        url: "https://www.faa.gov/newsroom/beyond-visual-line-sight-bvlos",
        note: "Agency page announcing the proposed BVLOS rule.",
      },
      {
        label: "FAA, BVLOS Fact Sheet (PDF)",
        url: "https://www.faa.gov/newsroom/fact_sheets/Fact_Sheet_BVLOS.pdf",
        note: "Source for the 400 feet AGL ceiling, enabled operations, and takeoff/landing/loading area requirements.",
      },
      {
        label: "FAA, Notice of Proposed Rulemaking, Docket FAA-2025-1908, RIN 2120-AL82 (PDF)",
        url: "https://www.faa.gov/newsroom/BVLOS_NPRM_website_version.pdf",
        note: "Proposed rule text and docket identifiers.",
      },
      {
        label: "United States v. Causby, 328 U.S. 256 (1946)",
        url: "https://tile.loc.gov/storage-services/service/ll/usrep/usrep328/usrep328256/usrep328256.pdf",
        note: "Library of Congress copy of the United States Reports opinion.",
      },
      {
        label: "Clark County, Nevada Recorder",
        url: "https://www.clarkcountynv.gov/government/elected_officials/county_recorder/index.php",
        note: "Recording office for the Las Vegas example.",
      },
    ],
    relatedQuestions: [
      "what-is-a-utility-easement-on-my-property",
      "how-to-find-easements-on-my-property",
      "how-wide-is-a-utility-easement",
      "do-easements-transfer-with-the-property",
    ],
    relatedStates: ["nevada", "texas", "california", "florida", "ohio"],
    relatedCountyGuides: [
      { state: "nevada", county: "clark", label: "Clark County, Nevada (Las Vegas)" },
    ],
  },
];

BLOG_POSTS.push(...BLOG_GUIDES, ...BLOG_RIGHTS_GUIDES, ...BLOG_LAND_USE_GUIDES, NORTH_DAKOTA_RIGHTS_POST, RURAL_LAND_DUE_DILIGENCE_POST);

/** Posts newest first. */
export const BLOG_POSTS_SORTED: BlogPost[] = [...BLOG_POSTS].sort((a, b) =>
  b.publishedOn.localeCompare(a.publishedOn),
);

export function findBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug);
}

export function blogPostsByTopic(topic: BlogTopic): BlogPost[] {
  return BLOG_POSTS_SORTED.filter((post) => post.topic === topic);
}

export function formatBlogDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
